import type { StateCreator } from 'zustand/vanilla';
import { DEFAULT_TTS_VOICE_ID, SITE } from '@/app/constants';
import type { FormAudioPrompt, ToolFormParams } from '@/app/types';
import {
  createEmptyExtensionToolDraft,
  createEmptyQuickEditToolDraft,
  type ExtensionToolDraft,
  type QuickEditToolDraft,
  type QuickEditToolMode,
} from '@/app/utils';

interface VoiceCloneFormParams {
  audioPrompt?: FormAudioPrompt;
}

export interface ToolsFormStore {
  upscaleVideoFormParams: ToolFormParams;
  upscaleImageFormParams: ToolFormParams;
  faceSwapFormParams: ToolFormParams;
  remakeFormParams: ToolFormParams;
  oneClickVideoProductionFormParams: ToolFormParams;
  oneClickMvFormParams: ToolFormParams;
  lipSyncFormParams: ToolFormParams;
  offlineAvatarFormParams: ToolFormParams;
  text2ttsFormParams: ToolFormParams;
  voiceCloneFormParams: VoiceCloneFormParams;
  styleTransferFormParams: ToolFormParams;
  motionControlFormParams: ToolFormParams;
  characterCreationFormParams: ToolFormParams;
  videoEditToolDraft: QuickEditToolDraft;
  imageEditToolDraft: QuickEditToolDraft;
  extensionToolDraft: ExtensionToolDraft;
  updateUpscaleVideoFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void;
  updateUpscaleImageFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void;
  updateFaceSwapFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void;
  updateRemakeFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void;
  updateOneClickVideoProductionFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void;
  updateOneClickMvFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void;
  updateLipSyncFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void;
  updateOfflineAvatarFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void;
  updateText2TtsFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void;
  updateVoiceCloneFormParams: (key: keyof VoiceCloneFormParams, value: VoiceCloneFormParams[typeof key]) => void;
  updateStyleTransferFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void;
  updateMotionControlFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void;
  updateCharacterCreationFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void;
  resetToolFormStates: () => void;
  replaceQuickEditToolDraft: (mode: QuickEditToolMode, draft: QuickEditToolDraft) => void;
  updateQuickEditToolDraft: (
    mode: QuickEditToolMode,
    updater: (draft: QuickEditToolDraft) => QuickEditToolDraft,
  ) => void;
  resetQuickEditToolDraft: (mode: QuickEditToolMode) => void;
  replaceExtensionToolDraft: (draft: ExtensionToolDraft) => void;
  updateExtensionToolDraft: (updater: (draft: ExtensionToolDraft) => ExtensionToolDraft) => void;
  resetExtensionToolDraft: () => void;
}

export type UpscaleVideoFormParams = ToolFormParams;
export type UpscaleImageFormParams = ToolFormParams;
export type FaceSwapFormParams = ToolFormParams;
export type RemakeFormParams = ToolFormParams;

const createDefaultUpscaleFormParams = (): ToolFormParams => ({
  imagePrompts: [],
  videoPrompts: [],
  audioPrompts: [],
  settings: {
    resolution: '2k',
  },
  creationId: undefined,
  textPrompt: '',
});

const defaultFaceSwapFormParams: ToolFormParams = {
  settings: {},
  imagePrompts: [],
  videoPrompts: [],
  audioPrompts: [],
  subtitleConfig: undefined,
  creationId: undefined,
  textPrompt: '',
};

const defaultRemakeFormParams: ToolFormParams = {
  settings: {
    retain_original_audio: true,
    aspect_ratio: '16:9',
    resolution: '1080p',
  },
  imagePrompts: [],
  videoPrompts: [],
  audioPrompts: [],
  subtitleConfig: undefined,
  creationId: undefined,
  textPrompt: '',
};

const defaultOneClickVideoProductionFormParams: ToolFormParams = {
  settings: {
    duration: 15,
    aspect_ratio: '16:9',
    biz_scene: 'general',
    resolution: '1080p',
    voice_lang: SITE === 'china' ? 'zh' : 'en',
  },
  imagePrompts: [],
  videoPrompts: [],
  audioPrompts: [],
  subtitleConfig: undefined,
  creationId: undefined,
  textPrompt: '',
};

const defaultOneClickMvFormParams: ToolFormParams = {
  settings: {
    aspect_ratio: '16:9',
    resolution: '1080p',
  } as ToolFormParams['settings'],
  imagePrompts: [],
  videoPrompts: [],
  audioPrompts: [],
  creationId: undefined,
  textPrompt: '',
  subtitlePrompts: [],
  referenceImagePrompts: [],
  oneClickMvVersion: 'v2',
  oneClickMvScene: 'sing',
  oneClickMvSubtitleEnabled: false,
};

const defaultLipSyncFormParams: ToolFormParams = {
  settings: {
    speed: 1.0,
    volume: 0,
    voice_id: DEFAULT_TTS_VOICE_ID,
  } as ToolFormParams['settings'],
  imagePrompts: [],
  videoPrompts: [],
  audioPrompts: [],
  creationId: undefined,
  textPrompt: '',
};

const defaultOfflineAvatarFormParams: ToolFormParams = {
  settings: {
    resolution: '540p',
    voice_id: DEFAULT_TTS_VOICE_ID,
  } as ToolFormParams['settings'],
  imagePrompts: [],
  videoPrompts: [],
  audioPrompts: [],
  creationId: undefined,
  textPrompt: '',
  offlineAvatarTimeline: [],
  offlineAvatar: undefined,
  offlineAvatarInputMode: 'audio',
};

const defaultTextToSpeechVoiceId = DEFAULT_TTS_VOICE_ID;

const defaultText2TtsFormParams: ToolFormParams = {
  settings: {
    speed: 1.0,
    pitch: 0,
    volume: 0,
    voice_id: defaultTextToSpeechVoiceId,
  } as ToolFormParams['settings'],
  imagePrompts: [],
  videoPrompts: [],
  audioPrompts: [],
  creationId: undefined,
  textPrompt: '',
};

const defaultStyleTransferFormParams: ToolFormParams = {
  settings: {
    aspect_ratio: '1:1',
    resolution: '1080p',
    sample_count: 1,
  } as ToolFormParams['settings'],
  imagePrompts: [],
  videoPrompts: [],
  audioPrompts: [],
  creationId: undefined,
  textPrompt: '',
};

const defaultMotionControlFormParams: ToolFormParams = {
  settings: {} as ToolFormParams['settings'],
  imagePrompts: [],
  videoPrompts: [],
  audioPrompts: [],
  creationId: undefined,
  textPrompt: '',
};

const defaultCharacterCreationFormParams: ToolFormParams = {
  settings: {
    sample_count: 1,
  } as ToolFormParams['settings'],
  imagePrompts: [],
  videoPrompts: [],
  audioPrompts: [],
  creationId: undefined,
  textPrompt: '',
};

const getDefaultToolFormState = () => ({
  upscaleVideoFormParams: createDefaultUpscaleFormParams(),
  upscaleImageFormParams: createDefaultUpscaleFormParams(),
  faceSwapFormParams: { ...defaultFaceSwapFormParams },
  remakeFormParams: { ...defaultRemakeFormParams },
  oneClickVideoProductionFormParams: { ...defaultOneClickVideoProductionFormParams },
  oneClickMvFormParams: { ...defaultOneClickMvFormParams },
  lipSyncFormParams: { ...defaultLipSyncFormParams },
  offlineAvatarFormParams: { ...defaultOfflineAvatarFormParams },
  text2ttsFormParams: { ...defaultText2TtsFormParams },
  voiceCloneFormParams: {},
  styleTransferFormParams: { ...defaultStyleTransferFormParams },
  motionControlFormParams: { ...defaultMotionControlFormParams },
  characterCreationFormParams: { ...defaultCharacterCreationFormParams },
});

export const createToolsFormSlice: StateCreator<ToolsFormStore, [], [], ToolsFormStore> = (set) => ({
  ...getDefaultToolFormState(),
  videoEditToolDraft: createEmptyQuickEditToolDraft('video'),
  imageEditToolDraft: createEmptyQuickEditToolDraft('image'),
  extensionToolDraft: createEmptyExtensionToolDraft(),

  updateUpscaleVideoFormParams: (key, params) => {
    set((state) => ({
      upscaleVideoFormParams: {
        ...state.upscaleVideoFormParams,
        [key]: params,
      },
    }));
  },

  updateUpscaleImageFormParams: (key, params) => {
    set((state) => ({
      upscaleImageFormParams: {
        ...state.upscaleImageFormParams,
        [key]: params,
      },
    }));
  },

  updateFaceSwapFormParams: (key, params) => {
    set((state) => ({
      faceSwapFormParams: {
        ...state.faceSwapFormParams,
        [key]: params,
      },
    }));
  },

  updateRemakeFormParams: (key, params) => {
    set((state) => ({
      remakeFormParams: {
        ...state.remakeFormParams,
        [key]: params,
      },
    }));
  },

  updateOneClickVideoProductionFormParams: (key, params) => {
    set((state) => ({
      oneClickVideoProductionFormParams: {
        ...state.oneClickVideoProductionFormParams,
        [key]: params,
      },
    }));
  },

  updateOneClickMvFormParams: (key, params) => {
    set((state) => ({
      oneClickMvFormParams: {
        ...state.oneClickMvFormParams,
        [key]: params,
      },
    }));
  },

  updateLipSyncFormParams: (key, params) => {
    set((state) => ({
      lipSyncFormParams: {
        ...state.lipSyncFormParams,
        [key]: params,
      },
    }));
  },

  updateOfflineAvatarFormParams: (key, params) => {
    set((state) => ({
      offlineAvatarFormParams: {
        ...state.offlineAvatarFormParams,
        [key]: params,
      },
    }));
  },

  updateText2TtsFormParams: (key, params) => {
    set((state) => ({
      text2ttsFormParams: {
        ...state.text2ttsFormParams,
        [key]: params,
      },
    }));
  },

  updateVoiceCloneFormParams: (key, value) => {
    set((state) => ({
      voiceCloneFormParams: {
        ...state.voiceCloneFormParams,
        [key]: value,
      },
    }));
  },

  updateStyleTransferFormParams: (key, params) => {
    set((state) => ({
      styleTransferFormParams: {
        ...state.styleTransferFormParams,
        [key]: params,
      },
    }));
  },

  updateMotionControlFormParams: (key, params) => {
    set((state) => ({
      motionControlFormParams: {
        ...state.motionControlFormParams,
        [key]: params,
      },
    }));
  },

  updateCharacterCreationFormParams: (key, params) => {
    set((state) => ({
      characterCreationFormParams: {
        ...state.characterCreationFormParams,
        [key]: params,
      },
    }));
  },

  resetToolFormStates: () => {
    set({
      ...getDefaultToolFormState(),
      videoEditToolDraft: createEmptyQuickEditToolDraft('video'),
      imageEditToolDraft: createEmptyQuickEditToolDraft('image'),
      extensionToolDraft: createEmptyExtensionToolDraft(),
    });
  },

  replaceQuickEditToolDraft: (mode, draft) => {
    set({
      [mode === 'video' ? 'videoEditToolDraft' : 'imageEditToolDraft']: {
        ...draft,
        dirty: false,
      },
    });
  },

  updateQuickEditToolDraft: (mode, updater) => {
    set((state) => {
      const key = mode === 'video' ? 'videoEditToolDraft' : 'imageEditToolDraft';
      return {
        [key]: {
          ...updater(state[key]),
          dirty: true,
        },
      };
    });
  },

  resetQuickEditToolDraft: (mode) => {
    set({
      [mode === 'video' ? 'videoEditToolDraft' : 'imageEditToolDraft']: createEmptyQuickEditToolDraft(mode),
    });
  },

  replaceExtensionToolDraft: (draft) => {
    set({
      extensionToolDraft: {
        ...draft,
        dirty: false,
      },
    });
  },

  updateExtensionToolDraft: (updater) => {
    set((state) => ({
      extensionToolDraft: {
        ...updater(state.extensionToolDraft),
        dirty: true,
      },
    }));
  },

  resetExtensionToolDraft: () => {
    set({
      extensionToolDraft: createEmptyExtensionToolDraft(),
    });
  },
});
